//#region IMPORTS
import {
  instoreTemplatesSelectors,
  clearInstoreTemplateParams,
  deleteInstoreTemplateAsync,
} from "../../features/instoreTemplates/instoreTemplatesSlice";
import {
  DELETE_TEMPLATE_SURE,
  COPY_TEMPLATE_SPECS_MODAL,
  CREATE_TEMPLATE_SPECS_MODAL,
  UPLOAD_DATA_FOR_INSTORE_TEMPLATE,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreOrdersSettingsDropdown from "../instoreOrders/InstoreOrdersSettingsDropdown";
import InstoreTemplateDataUpload from "./InstoreTemplateDataUpload";
import InstoreTemplatesFilterBar from "./InstoreTemplatesFilterBar";
import InstoreTemplatesTable from "./InstoreTemplatesTable";
import InstoreTemplateForm from "./InstoreTemplateForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import ActionButton from "../genericButtons/actionButton/ActionButton";
import useHQmanager from "../../hooks/useHQmanager";
import AreYouSure from "../modalTypes/AreYouSure";
import PageHeader from "../genericPageHeader/PageHeader";
import MainModal from "../_layout/MainModal";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function InstoreTemplatesContainer({
  title = "rituals.instoreTemplates",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const instoreTemplates = useAppSelector(instoreTemplatesSelectors.selectAll);
  const { loading, instoreTemplates_Loaded, metaData, status } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates,
  );
  const { user } = useAppSelector(({ account }) => account);

  // Local state
  const [savingDataUpload] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InstoreTemplate>();

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);
  //#endregion

  //#region LOGIC
  // Create new template
  const createNewTemplateHandler = () => {
    dispatch(openModal(CREATE_TEMPLATE_SPECS_MODAL));
  };

  // Upload new template
  const uploadNewTemplateHandler = () => {
    dispatch(openModal(UPLOAD_DATA_FOR_INSTORE_TEMPLATE));
  };

  // Update or copy template
  const updateOrCopyTemplateHandler = (
    instoreTemplate: InstoreTemplate,
    copy: boolean,
  ) => {
    setSelectedTemplate(instoreTemplate);
    if (!copy) {
      history.push(`/instoretemplates/${instoreTemplate.id}`, true);
    } else {
      dispatch(openModal(COPY_TEMPLATE_SPECS_MODAL));
    }
  };

  // Delete template warning
  const deleteTemplateWarningHandler = (instoreTemplate: InstoreTemplate) => {
    setSelectedTemplate(instoreTemplate);
    dispatch(openModal(DELETE_TEMPLATE_SURE));
  };

  // Delete template confirm
  const deleteTemplateHandler = async () => {
    if (selectedTemplate) {
      await dispatch(deleteInstoreTemplateAsync({ id: selectedTemplate.id }));
      dispatch(openModal(false));
    }
  };

  // Cancel create or update promo
  const cancelCRUDTemplate = () => {
    if (!savingDataUpload) {
      setSelectedTemplate(undefined);
      dispatch(openModal(false));
    } else {
      toast.error("Some background tasks are stil running. Please wait...");
    }
  };

  // Reset handler
  const resetHandler = () => {
    setSelectedTemplate(undefined);
    dispatch(clearInstoreTemplateParams());
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={DELETE_TEMPLATE_SURE} closeHandler={cancelCRUDTemplate}>
        <AreYouSure
          confirmHandler={deleteTemplateHandler}
          closeHandler={cancelCRUDTemplate}
          message={`Are you sure you want to delete template ${selectedTemplate?.name}?`}
          title="Delete template?"
        />
      </MainModal>

      <MainModal
        type={COPY_TEMPLATE_SPECS_MODAL}
        closeHandler={cancelCRUDTemplate}
        closeDisabled={
          loading &&
          (status.includes("PendingCreateInstoreTemplate") ||
            status.includes("PendingUpdateInstoreTemplate"))
        }
      >
        <InstoreTemplateForm
          instoreTemplateId={selectedTemplate?.id}
          cancelCUD={cancelCRUDTemplate}
          isCopy
          disabled={
            loading &&
            (status.includes("PendingCreateInstoreTemplate") ||
              status.includes("PendingUpdateInstoreTemplate"))
          }
        />
      </MainModal>

      <MainModal
        type={CREATE_TEMPLATE_SPECS_MODAL}
        closeHandler={cancelCRUDTemplate}
        closeDisabled={loading && status.includes("PendingCreateInstoreTemplate")}
      >
        <InstoreTemplateForm
          cancelCUD={cancelCRUDTemplate}
          create
          disabled={loading && status.includes("PendingCreateInstoreTemplate")}
        />
      </MainModal>

      <MainModal
        type={UPLOAD_DATA_FOR_INSTORE_TEMPLATE}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreTemplateDataUpload
          cancelCUD={cancelCRUDTemplate}
          saving={savingDataUpload}
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          createClickHandler={createNewTemplateHandler}
          createButtonValue={t("rituals.addTemplate")}
          extraHQButtons={
            <Fragment>
              <InstoreOrdersSettingsDropdown
                hide={!isAdmin}
                selectedLink="templates"
              />
              <ActionButton
                clickHandler={uploadNewTemplateHandler}
                className="bg-lime-700 hover:bg-lime-500 focus-visible:outline-lime-700"
                disabled={loading}
                value={t("rituals.importTemplate")}
              />
            </Fragment>
          }
          disabled={loading}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          hqCanAdd
          title={t(title)}
          reset={resetHandler}
        />

        <InstoreTemplatesFilterBar instoreTemplates={instoreTemplates} />

        <InstoreTemplatesTable
          editOrCopyHandler={(item: InstoreTemplate, copy: boolean) =>
            updateOrCopyTemplateHandler(item, copy)
          }
          deleteHandler={(item: InstoreTemplate) =>
            deleteTemplateWarningHandler(item)
          }
          itemsLoaded={instoreTemplates_Loaded}
          metaData={metaData}
          loading={loading}
          isAdmin={isAdmin}
          items={instoreTemplates}
          hide={instoreTemplates_Loaded && instoreTemplates.length <= 0}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
